/**
 * Submit Invoice Modal
 * Record submission details when sending invoice to insurance company
 */

import React, { useState } from 'react';
import { X, Send, AlertCircle, CheckCircle } from 'lucide-react';
import { MonthlyInvoiceDetail } from '../../services/monthlyInvoiceService';
import monthlyInvoiceService from '../../services/monthlyInvoiceService';

interface SubmitInvoiceModalProps {
  invoice: MonthlyInvoiceDetail;
  onClose: () => void;
  onSubmitted: () => void;
}

const SubmitInvoiceModal: React.FC<SubmitInvoiceModalProps> = ({ invoice, onClose, onSubmitted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    submission_method: 'Email',
    submission_reference: '',
    expected_payment_date: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await monthlyInvoiceService.submitInvoice(invoice.id, formData);
      setSuccess(true);
      // Give user time to see success message before closing
      setTimeout(() => {
        onSubmitted();
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la soumission');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Soumettre Facture</h2>
            <p className="text-sm text-gray-600 mt-1">{invoice.numero}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={loading}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Méthode de soumission *
            </label>
            <select
              value={formData.submission_method}
              onChange={(e) => setFormData({ ...formData, submission_method: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
            >
              <option value="Email">Email</option>
              <option value="Courrier">Courrier</option>
              <option value="En main propre">En main propre</option>
              <option value="Plateforme en ligne">Plateforme en ligne</option>
              <option value="Autre">Autre</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Référence de soumission
            </label>
            <input
              type="text"
              value={formData.submission_reference}
              onChange={(e) => setFormData({ ...formData, submission_reference: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="N° de tracking, référence email, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de paiement prévue
            </label>
            <input
              type="date"
              value={formData.expected_payment_date}
              onChange={(e) => setFormData({ ...formData, expected_payment_date: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <p className="text-xs text-gray-500 mt-1">Optionnel - pour le suivi des retards</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Informations complémentaires..."
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">Facture soumise avec succès!</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 disabled:opacity-50"
              disabled={loading || success}
            >
              <Send className="w-4 h-4" />
              {success ? 'Soumise ✓' : loading ? 'Soumission...' : 'Soumettre'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitInvoiceModal;
